import { useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import "./engine-workbench.css";

const PROFILE = "ZT-ENGINE-MECHANICS-1.0.0";
const FIXTURE_IDS = [
  "arithmetic-basic",
  "decimal-rounding",
  "conditional-state",
] as const;
type FixtureId = (typeof FIXTURE_IDS)[number];
interface Fixture {
  id: FixtureId;
  name: string;
  description: string;
}
interface Qualification {
  profile: typeof PROFILE;
  fixtures: Fixture[];
  capabilities: {
    syntheticExecution: true;
    taxPreparation: false;
    certification: false;
  };
}
interface EngineRun {
  runId: string;
  fixtureId: FixtureId;
  status: "PASSED" | "FAILED";
  checks: { name: string; passed: boolean }[];
  resultDigest: string;
  traceDigest: string;
  planDigest: string;
  results: {
    label: string;
    value: string | number | boolean | null;
    state: "KNOWN" | "UNKNOWN" | "CONFLICT";
  }[];
  createdAt: string;
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function boundedText(value: unknown, maximum = 300): value is string {
  return (
    typeof value === "string" &&
    value.trim().length > 0 &&
    value.length <= maximum
  );
}
function fixtureId(value: unknown): value is FixtureId {
  return FIXTURE_IDS.includes(value as FixtureId);
}
function invalidResponse(): never {
  throw new Error("ENGINE_RESPONSE_INVALID");
}

export function validateEngineQualification(value: unknown): Qualification {
  if (
    !record(value) ||
    value.profile !== PROFILE ||
    !record(value.capabilities) ||
    value.capabilities.syntheticExecution !== true ||
    value.capabilities.taxPreparation !== false ||
    value.capabilities.certification !== false ||
    !Array.isArray(value.fixtures) ||
    value.fixtures.length !== FIXTURE_IDS.length ||
    value.fixtures.some(
      (item) =>
        !record(item) ||
        !fixtureId(item.id) ||
        !boundedText(item.name, 100) ||
        !boundedText(item.description),
    ) ||
    new Set(value.fixtures.map((item) => item.id)).size !== FIXTURE_IDS.length
  ) {
    return invalidResponse();
  }
  return value as unknown as Qualification;
}

export function validateEngineRun(
  value: unknown,
  requestedFixture: FixtureId,
): EngineRun {
  const digest = (item: unknown) =>
    typeof item === "string" && /^[a-f0-9]{64}$/i.test(item);
  if (
    !record(value) ||
    !boundedText(value.runId, 128) ||
    value.fixtureId !== requestedFixture ||
    !["PASSED", "FAILED"].includes(value.status as string) ||
    !Array.isArray(value.checks) ||
    value.checks.length < 1 ||
    value.checks.length > 64 ||
    value.checks.some(
      (item) =>
        !record(item) ||
        !boundedText(item.name) ||
        typeof item.passed !== "boolean",
    ) ||
    (value.status === "PASSED") !== value.checks.every((item) => item.passed) ||
    !digest(value.resultDigest) ||
    !digest(value.traceDigest) ||
    !digest(value.planDigest) ||
    !Array.isArray(value.results) ||
    value.results.length > 64 ||
    value.results.some(
      (item) =>
        !record(item) ||
        !boundedText(item.label) ||
        !["KNOWN", "UNKNOWN", "CONFLICT"].includes(item.state as string) ||
        !(
          item.value === null ||
          typeof item.value === "boolean" ||
          (typeof item.value === "string" && item.value.length <= 1000) ||
          (typeof item.value === "number" && Number.isFinite(item.value))
        ),
    ) ||
    !boundedText(value.createdAt, 64) ||
    !Number.isFinite(Date.parse(value.createdAt)) ||
    (Object.hasOwn(value, "syntheticOnly") && value.syntheticOnly !== true) ||
    (Object.hasOwn(value, "profile") && value.profile !== PROFILE) ||
    (Object.hasOwn(value, "taxPreparation") &&
      value.taxPreparation !== false) ||
    (Object.hasOwn(value, "certification") && value.certification !== false)
  ) {
    return invalidResponse();
  }
  return value as unknown as EngineRun;
}

function requestMessage(error: unknown): string {
  if (record(error) && error.status === 401) {
    return "Your session expired. Sign in again before running engine checks.";
  }
  if (record(error) && error.status === 403) {
    return "Administrator access is required to run these engine checks.";
  }
  return "The engine check could not be completed or verified. No taxpayer return was changed. Try again; if this continues, contact your administrator.";
}

export interface EngineWorkbenchProps {
  isAdministrator: boolean;
  /** Authenticated user and workspace scope; changing it discards all local run state. */
  sessionScope: string;
}

export function EngineWorkbench({
  isAdministrator,
  sessionScope,
}: EngineWorkbenchProps) {
  if (!isAdministrator || !sessionScope) {
    return (
      <section
        className="engine-workbench ew-restricted"
        aria-label="Engine checks"
      >
        <h3>Administrator access required</h3>
        <p>
          Local engine checks are available only to administrators in the
          advanced workspace.
        </p>
      </section>
    );
  }
  // A new authentication scope must never reuse the previous scope's run state.
  return (
    <AdministratorWorkbench key={sessionScope} sessionScope={sessionScope} />
  );
}

function AdministratorWorkbench({ sessionScope }: { sessionScope: string }) {
  const headingId = useId();
  const fixtureSelectId = useId();
  const descriptionId = useId();
  const [selected, setSelected] = useState<FixtureId>("arithmetic-basic");
  const [pending, setPending] = useState(false);
  const [run, setRun] = useState<EngineRun | null>(null);
  const [runError, setRunError] = useState("");
  const activeRequest = useRef<AbortController | null>(null);
  const query = useQuery({
    queryKey: ["engine-qualification", PROFILE, sessionScope],
    queryFn: async ({ signal }) =>
      validateEngineQualification(
        await api<unknown>("/engine/qualification", { method: "GET", signal }),
      ),
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: undefined,
    gcTime: 0,
  });
  const qualification =
    !query.isError && !query.isPending ? query.data : undefined;
  const selectedFixture = qualification?.fixtures.find(
    (item) => item.id === selected,
  );

  useEffect(
    () => () => {
      activeRequest.current?.abort();
      activeRequest.current = null;
    },
    [],
  );

  async function runChecks() {
    if (!selectedFixture || activeRequest.current) return;
    const controller = new AbortController();
    activeRequest.current = controller;
    setPending(true);
    setRun(null);
    setRunError("");
    try {
      const response = await api<unknown>("/engine/qualification/runs", {
        method: "POST",
        signal: controller.signal,
        body: JSON.stringify({ fixtureId: selectedFixture.id }),
      });
      const verified = validateEngineRun(response, selectedFixture.id);
      if (!controller.signal.aborted && activeRequest.current === controller)
        setRun(verified);
    } catch (error) {
      if (!controller.signal.aborted && activeRequest.current === controller)
        setRunError(requestMessage(error));
    } finally {
      if (!controller.signal.aborted && activeRequest.current === controller) {
        activeRequest.current = null;
        setPending(false);
      }
    }
  }

  return (
    <section className="engine-workbench" aria-labelledby={headingId}>
      <header className="ew-header">
        <div>
          <p className="ew-eyebrow">Local engine checks</p>
          <h3 id={headingId}>Engine workbench</h3>
        </div>
        <span className="ew-badge">Synthetic only</span>
      </header>
      <div className="ew-boundary">
        <strong>Not tax preparation or certification</strong>
        <p>
          These checks exercise compiler and evaluator mechanics using fixed
          synthetic examples. They do not use taxpayer information, calculate a
          return, verify tax law, or certify a product or return.
        </p>
      </div>
      {query.isPending ? (
        <p className="ew-state" role="status">
          Loading available engine checks…
        </p>
      ) : query.isError ? (
        <div className="ew-state ew-error" role="alert">
          <strong>Engine checks are unavailable</strong>
          <p>
            No check has been started. Confirm your administrator access and try
            again.
          </p>
          <button
            type="button"
            className="ew-secondary"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? "Trying again…" : "Try again"}
          </button>
        </div>
      ) : qualification ? (
        <>
          <div className="ew-controls">
            <div className="ew-fixture">
              <label htmlFor={fixtureSelectId}>Synthetic example</label>
              <select
                id={fixtureSelectId}
                value={selected}
                aria-describedby={descriptionId}
                disabled={pending}
                onChange={(event) => {
                  if (!pending && fixtureId(event.target.value)) {
                    setSelected(event.target.value);
                    setRun(null);
                    setRunError("");
                  }
                }}
              >
                {qualification.fixtures.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <p id={descriptionId}>{selectedFixture?.description}</p>
            </div>
            <button
              type="button"
              className="ew-run"
              disabled={pending || !selectedFixture}
              onClick={() => void runChecks()}
            >
              {pending ? "Running checks…" : "Run checks"}
            </button>
          </div>
          <div
            className="ew-run-state"
            aria-live="polite"
            aria-atomic="false"
            aria-busy={pending}
          >
            {pending && (
              <p role="status">
                Running the selected synthetic example. No taxpayer return is
                being processed.
              </p>
            )}
            {runError && (
              <p className="ew-error" role="alert">
                {runError}
              </p>
            )}
            {!pending && !runError && !run && (
              <p className="ew-state">No check has been run in this session.</p>
            )}
            {!pending && !runError && run && (
              <div className="ew-result">
                <h4
                  className={
                    run.status === "PASSED" ? "ew-passed" : "ew-failed"
                  }
                >
                  {run.status === "PASSED"
                    ? "Synthetic mechanics checks passed"
                    : "Synthetic mechanics checks failed"}
                </h4>
                <p>
                  {run.checks.filter((item) => item.passed).length} of{" "}
                  {run.checks.length} checks passed. This outcome is not a tax
                  or certification decision.
                </p>
                <ul className="ew-checks" aria-label="Synthetic check results">
                  {run.checks.map((check, index) => (
                    <li key={index}>
                      <span
                        className={check.passed ? "ew-passed" : "ew-failed"}
                      >
                        {check.passed ? "Passed" : "Failed"}
                      </span>
                      <span>{check.name}</span>
                    </li>
                  ))}
                </ul>
                {run.results.length > 0 && (
                  <div className="ew-values">
                    <h4>Synthetic outputs</h4>
                    <dl>
                      {run.results.map((item, index) => (
                        <div key={index}>
                          <dt>{item.label}</dt>
                          <dd>
                            <span>
                              {item.value === null
                                ? "No value"
                                : String(item.value)}
                            </span>
                            <small>
                              {item.state === "KNOWN"
                                ? "Known"
                                : item.state === "UNKNOWN"
                                  ? "Unknown"
                                  : "Conflicting"}
                            </small>
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
                <details className="ew-technical">
                  <summary>Technical details</summary>
                  <dl>
                    <div>
                      <dt>Profile</dt>
                      <dd>{qualification.profile}</dd>
                    </div>
                    <div>
                      <dt>Run identifier</dt>
                      <dd>{run.runId}</dd>
                    </div>
                    <div>
                      <dt>Fixture identifier</dt>
                      <dd>{run.fixtureId}</dd>
                    </div>
                    <div>
                      <dt>Recorded at</dt>
                      <dd>
                        <time dateTime={run.createdAt}>{run.createdAt}</time>
                      </dd>
                    </div>
                    <div>
                      <dt>Compiled plan digest</dt>
                      <dd>
                        <code>{run.planDigest}</code>
                      </dd>
                    </div>
                    <div>
                      <dt>Result digest</dt>
                      <dd>
                        <code>{run.resultDigest}</code>
                      </dd>
                    </div>
                    <div>
                      <dt>Execution trace digest</dt>
                      <dd>
                        <code>{run.traceDigest}</code>
                      </dd>
                    </div>
                  </dl>
                  <p>
                    Digests identify these artifacts. Displaying a digest does
                    not establish independent verification or certification.
                  </p>
                </details>
              </div>
            )}
          </div>
        </>
      ) : (
        <p className="ew-state" role="status">
          No engine check is available. Nothing has been executed.
        </p>
      )}
    </section>
  );
}
